import type { University } from "@src/domain/universities/entities/university.entity";
import type { UniversityRepository } from "@src/domain/universities/repository/university_repository";
import type { ApiClient } from "../api/client";
import { UniversityMapper } from "../mappers/university_mapper";

/**
 * University Repository Implementation
 * Handles HTTP communication for university management operations
 */
export class UniversityRepositoryImpl implements UniversityRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Create a new university
     */
    async create(university: University): Promise<void> {
        const dto = UniversityMapper.toPersistence(university);
        await this.apiClient.post("/universities", dto);
    }

    /**
     * Find university by ID
     */
    async findById(id: string): Promise<University | null> {
        try {
            const data = await this.apiClient.get<any>(`/universities/${id}`);
            return data ? UniversityMapper.toDomain(data) : null;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Find all universities with pagination
     */
    async findAll(skip?: number, limit?: number): Promise<{ universities: University[]; total: number }> {
        const queryParams = new URLSearchParams();

        if (skip !== undefined) {
            queryParams.append('skip', skip.toString());
        }
        if (limit !== undefined) {
            queryParams.append('limit', limit.toString());
        }

        const queryString = queryParams.toString();
        const url = queryString ? `/universities?${queryString}` : '/universities';

        const response = await this.apiClient.get<any>(url);

        // Handle paginated response
        const universities = response.universities || response.data || response || [];
        const total = response.total || universities.length;

        return {
            universities: Array.isArray(universities) ? universities.map((u: any) => UniversityMapper.toDomain(u)) : [],
            total,
        };
    }

    /**
     * Update university
     */
    async update(university: University): Promise<void> {
        const dto = UniversityMapper.toUpdate(university);
        await this.apiClient.put(`/universities/${university.universityId}`, dto);
    }

    /**
     * Delete university
     */
    async delete(id: string): Promise<void> {
        await this.apiClient.delete(`/universities/${id}`);
    }

    /**
     * Find university by code
     */
    async findByCode(code: string): Promise<University | null> {
        const { universities } = await this.findAll();
        return universities.find(u => u.code === code) || null;
    }

    /**
     * Find all active universities
     */
    async findActive(): Promise<University[]> {
        const { universities } = await this.findAll();
        return universities.filter(u => u.isActive);
    }
}